import React, { useState, useEffect } from "react";
import {
  Button,
  Card,
  Typography,
  Grid,
  CardContent,
  CardMedia,
  Box,
  Chip,
  Avatar,
  Container,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  Breadcrumbs,
  Link as MuiLink
} from "@mui/material";
import { Search } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import NewsletterModal from "../components/Modals/NewsletterModal";
import UserQuery from "../stateQueries/User";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";

const Articles = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const articlesPerPage = 12;
  const [openNewsletterModal, setOpenNewsletterModal] = useState(false);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1); // Reset to first page when search changes
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: [
      "articles",
      currentPage,
      articlesPerPage,
      debouncedSearchTerm,
      selectedCategory
    ],
    queryFn: async () =>
      await UserQuery.getAllArticles(
        currentPage,
        articlesPerPage,
        debouncedSearchTerm,
        selectedCategory === "All" ? "" : selectedCategory
      )
  });

  console.log(data);

  const articles = data?.articles?.articles || [];
  const totalPages = data?.articles?.totalPages || 1;

  const categories = [
    "All",
    ...new Set(
      articles?.flatMap((post) =>
        post.tags ? post.tags.map((tag) => parseTag(tag).label) : []
      )
    )
  ];

  // Filter articles by category if not "All"
  const filteredArticles =
    selectedCategory === "All"
      ? articles
      : articles?.filter((post) => {
          return post.tags?.some((tag) => {
            const parsedTag = parseTag(tag);
            return parsedTag.label === selectedCategory;
          });
        });

  // Safely parse a tag that may be a plain string or a JSON {value,label} object
  function parseTag(tag) {
    try { return JSON.parse(tag); } catch { return { value: tag, label: tag }; }
  }

  // Resolve article cover image URL (handles absolute URLs and both upload folders)
  function articleImageSrc(imageURL) {
    if (!imageURL) return "";
    if (imageURL.startsWith("http")) return imageURL;
    return `${process.env.REACT_APP_API_URL}/uploads/articles/${imageURL}`;
  }

  useEffect(() => {
    setOpenNewsletterModal(true);
  }, []);

  const handleCloseNewsletterModal = () => {
    setOpenNewsletterModal(false);
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBackToHome = () => {
    navigate("/home");
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 4 }}>
        <MuiLink
          component="button"
          variant="body1"
          onClick={handleBackToHome}
          sx={{ cursor: "pointer", color: "primary.main" }}
        >
          Home
        </MuiLink>
        <Typography color="text.primary">Articles</Typography>
      </Breadcrumbs>

      {/* Header Section */}
      <Box sx={{ textAlign: "center", mb: 6 }}>
        <Typography
          variant="h2"
          component="h1"
          fontWeight="bold"
          sx={{
            color: "primary.main",
            mb: 2,
            fontSize: { xs: "2.5rem", md: "3.5rem", lg: "4rem" }
          }}
        >
          Latest Articles & News
        </Typography>
        <Typography
          variant="h5"
          sx={{
            color: "text.secondary",
            maxWidth: 800,
            mx: "auto",
            px: 2,
            lineHeight: 1.6
          }}
        >
          Stay informed with the latest insights, trends, and developments in
          the financial services sector. Discover expert analysis, industry
          updates, and thought leadership content.
        </Typography>
      </Box>

      {/* Search and Filter Section */}
      <Box sx={{ mb: 6 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search articles by title, content, or author..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="primary" />
                  </InputAdornment>
                )
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 3,
                  "&:hover fieldset": {
                    borderColor: "primary.main"
                  }
                }
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={selectedCategory}
                label="Category"
                onChange={(e) => setSelectedCategory(e.target.value)}
                sx={{ borderRadius: 3 }}
              >
                {categories.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
            >
              {filteredArticles?.length} articles found{" "}
              {debouncedSearchTerm && `for "${debouncedSearchTerm}"`}
            </Typography>
          </Grid>
        </Grid>
      </Box>

      {/* All Articles Grid */}
      <Box sx={{ mb: 6 }}>
        <Typography
          variant="h4"
          component="h2"
          fontWeight="bold"
          sx={{ mb: 3, color: "primary.main" }}
        >
          {selectedCategory === "All"
            ? "All Articles"
            : `${selectedCategory} Articles`}
        </Typography>

        {isLoading ? (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <Typography variant="h6" color="text.secondary">
              Loading articles...
            </Typography>
          </Box>
        ) : isError ? (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <Typography variant="h6" color="error.main" sx={{ mb: 2 }}>
              Error: {error.message}
            </Typography>
            <Button variant="outlined" onClick={() => window.location.reload()}>
              Retry
            </Button>
          </Box>
        ) : filteredArticles?.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
              No articles found matching your criteria
            </Typography>
            <Button
              variant="outlined"
              onClick={() => {
                setSearchTerm("");
                setSelectedCategory("All");
                setCurrentPage(1);
              }}
            >
              Clear Filters
            </Button>
          </Box>
        ) : (
          <>
            <Grid container spacing={3}>
              {filteredArticles?.map((post) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={post.id}>
                  <Card
                    onClick={() => navigate(`/article/${post.id}`)}
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      cursor: "pointer",
                      transition: "all 0.3s ease-in-out",
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow: "0 20px 40px rgba(0,0,0,0.1)"
                      },
                      borderRadius: 3,
                      overflow: "hidden"
                    }}
                  >
                    <CardMedia
                      component="img"
                      height="200"
                      image={articleImageSrc(post?.imageURL)}
                      alt={post.title}
                      sx={{ objectFit: "cover" }}
                    />
                    <CardContent sx={{ flexGrow: 1, p: 3 }}>
                      {post.tags?.length > 0 && (
                      <Chip
                        label={parseTag(post.tags[0]).label}
                        size="small"
                        sx={{
                          backgroundColor: "primary.light",
                          color: "white",
                          mb: 2,
                          fontWeight: 500
                        }}
                      />
                      )}
                      <Typography
                        variant="h6"
                        component="h3"
                        fontWeight="bold"
                        sx={{
                          mb: 2,
                          lineHeight: 1.3,
                          fontSize: { xs: "1rem", md: "1.1rem" },
                          minHeight: "2.6em",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden"
                        }}
                      >
                        {post.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mb: 3,
                          lineHeight: 1.6,
                          display: "-webkit-box",
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden"
                        }}
                      >
                        {post.subTitle}
                      </Typography>

                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 2 }}
                      >
                        <Avatar
                          sx={{
                            width: 24,
                            height: 24,
                            fontSize: "0.75rem",
                            backgroundColor: "primary.main",
                            mr: 1
                          }}
                        >
                          {post.author.charAt(0)}
                        </Avatar>
                        <Typography variant="caption" color="text.secondary">
                          {post.author}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center"
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          {format(new Date(post.createdAt), "MMMM dd, yyyy")}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="primary.main"
                          fontWeight="500"
                        >
                          {`${post.readTimeValue} ${post.readTimeUnit}`}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* Pagination */}
            {totalPages > 1 && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  size="large"
                  showFirstButton
                  showLastButton
                  sx={{
                    "& .MuiPaginationItem-root": {
                      borderRadius: 2,
                      fontSize: "1.1rem"
                    }
                  }}
                />
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Back to Home Button */}
      <Box sx={{ textAlign: "center", mt: 6 }}>
        <Button
          variant="contained"
          size="large"
          onClick={handleBackToHome}
          sx={{
            backgroundColor: "primary.main",
            color: "white",
            px: 4,
            py: 1.5,
            borderRadius: 3,
            fontSize: "1.1rem",
            fontWeight: 600,
            textTransform: "none",
            boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
            transition: "all 0.3s ease",
            "&:hover": {
              backgroundColor: "primary.dark",
              transform: "translateY(-2px)",
              boxShadow: "0 12px 35px rgba(0,0,0,0.2)"
            }
          }}
        >
          Back to Home
        </Button>
      </Box>

      <NewsletterModal
        open={openNewsletterModal}
        handleClose={handleCloseNewsletterModal}
      />
    </Container>
  );
};

export default Articles;
